import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { ProductDetailsPageContext } from "@insite/content-library/Pages/ProductDetailsPage";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import File from "@insite/mobius/Icons/File";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CheckCircle from "@tJohnstonCompanies/Icons/CheckCircle";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = HasProduct;
export enum pdpNavigationIds {
    description = "description",
    specifications = "specifications",
    features = "features",
    documents = "documents",
    additionalInfo = "additionalInfo",
}
export interface PDPNavigationStyles {
    navigationButton?: InjectableCss;
    navWrapper?: InjectableCss;
    parentWrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    descriptionSection?: InjectableCss;
    specificationWrapper?: InjectableCss;
    featuresWrapper?: InjectableCss;
    documentsWrapper?: InjectableCss;
    additionInfoWrapper?: InjectableCss;
    titleCss?: InjectableCss;

    attributeTypeLabelText?: TypographyPresentationProps;
    attributeTypeValuesText?: TypographyPresentationProps;
    attributeTypeWrapper?: InjectableCss;

    wrapper?: InjectableCss;
    container?: GridContainerProps;
    documentGridItem?: GridItemProps;
    documentLink?: LinkPresentationProps;
    documentIcon?: IconProps;
    documentNameText?: TypographyPresentationProps;
    featureLi?: InjectableCss;
}
interface Navigation {
    title: string;
    link: string;
}
interface NavigationList {
    navigation: Navigation[];
}

interface AdditionalInfo {
    title: string;
    value: any;
}
interface AdditionalInfoList {
    additionalInfo: AdditionalInfo[];
}
interface Section {
    top: number;
    bottom: number;
}

export const pdpNavigationStyles: PDPNavigationStyles = {
    parentWrapper: {
        css: css`
            padding: 20px;
        `,
    },
    navWrapper: {
        css: css`
            display: flex;
            margin-top: 20px;
            border-top: 2px solid #e0e0e0;
            border-bottom: 2px solid #e0e0e0;
            padding: 12px 5px;
            overflow: auto;
        `,
    },
    navigationButton: {
        css: css`
            background: transparent;
            border: none;
            margin-right: 24px;
            padding: 0px;
            min-height: 20px;
            color: #333;
            text-transform: uppercase;
            span {
                font-weight: 700;
                font-size: 16px;
                line-height: 19px;
                border-right: 2px solid #333;
                padding-right: 20px;
                white-space: nowrap;
            }
            &:hover {
                background: transparent;
            }
            &:focus {
                outline: none;
            }
        `,
    },

    sectionWrapper: {
        css: css`
            padding: 10px;
        `,
    },

    descriptionSection: {
        css: css`
            padding: 5px 0;
            font-size: 14px;
            font-weight: 500;
        `,
    },

    titleCss: {
        css: css`
            font-weight: 700;
            font-size: 22px;
            line-height: 40px;
        `,
    },

    attributeTypeLabelText: {
        css: css`
            font-weight: 500;
            letter-spacing: 0;
            text-align: justify;
            color: #a8a8a8;
        `,
    },
    attributeTypeValuesText: {
        css: css`
            color: #333;
            font-weight: 300;
            letter-spacing: 0;
            text-align: justify;
        `,
    },
    attributeTypeWrapper: {
        css: css`
            display: flex;
        `,
    },

    featuresWrapper: {
        css: css`
            display: flex;
            gap: 10px;
        `,
    },

    wrapper: {
        css: css`
            padding: 12px 0;
        `,
    },
    documentGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            > iframe {
                height: 300px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            height: 220px;
                        `,
                        css`
                            height: 220px;
                        `,
                        css`
                            height: 240px;
                        `,
                        css`
                            width: 85%;
                            height: 250px;
                        `,
                        null,
                    ])}
            }
        `,
    },
    documentIcon: {
        src: File,
        color: "#c0c0c0",
        css: css`
            margin-right: 5px;
            height: 30px;
            width: 30px;
            > svg {
                height: 30px;
                width: 30px;
            }
        `,
    },
    documentNameText: {
        css: css`
            vertical-align: super;
            color: #333;
            font-size: 16px;
            font-weight: 500;
        `,
    },

    featureLi: {
        css: css`
            > ul {
                list-style-type: none;
                padding: 0 0 0 10px;
                margin: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                padding: 0;
                            `,
                            css`
                                padding: 0;
                            `,
                            null,
                            null,
                        ],
                        "minMax",
                    )}
                > li {
                    &::before {
                        padding-right: 10px;
                        content: url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23A21010' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' className='feather feather-check-circle'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'%3E%3C/path%3E%3Cpolyline points='22 4 12 14.01 9 11.01'%3E%3C/polyline%3E%3C/svg%3E%0A");
                    }
                }
            }
            > html {
                > body {
                    > ul {
                        list-style-type: none;
                        padding: 0 0 0 10px;
                        margin: 0;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        padding: 0;
                                    `,
                                    css`
                                        padding: 0;
                                    `,
                                    null,
                                    null,
                                ],
                                "minMax",
                            )}
                        > li {
                            &::before {
                                padding-right: 10px;
                                content: url("data:image/svg+xml, %3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23A21010' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' className='feather feather-check-circle'%3E%3Cpath d='M22 11.08V12a10 10 0 1 1-5.93-9.14'%3E%3C/path%3E%3Cpolyline points='22 4 12 14.01 9 11.01'%3E%3C/polyline%3E%3C/svg%3E%0A");
                            }
                        }
                    }
                }
            }
        `,
    },
};

const styles = pdpNavigationStyles;

const StyledUl = getStyledWrapper("ul");
const StyledLi = getStyledWrapper("li");

const ProductDetailPageNavigation: React.FC<Props> = ({ product }) => {
    // Adding changes for hide additional info when there is no value.
    const newAdditionalInfo = [];
    product?.packDescription
        ? newAdditionalInfo.push({
              title: product?.packDescription === "" ? "" : "Package Description",
              value: product?.packDescription === "" ? "" : product?.packDescription,
          })
        : "";

    product?.detail?.unspsc
        ? newAdditionalInfo.push({
              title: product?.detail?.unspsc === "" ? "" : "UNSPSC",
              value: product?.detail?.unspsc === "" ? "" : product?.detail?.unspsc,
          })
        : "";

    product?.detail?.shippingLength
        ? newAdditionalInfo.push({
              title: product?.detail?.shippingLength === 0 ? "" : "Package Length",
              value: product?.detail?.shippingLength === 0 ? "" : product?.detail?.shippingLength,
          })
        : "";

    product?.detail?.shippingWidth
        ? newAdditionalInfo.push({
              title: product?.detail?.shippingWidth === 0 ? "" : "Package Width",
              value: product?.detail?.shippingWidth === 0 ? "" : product?.detail?.shippingWidth,
          })
        : "";

    product?.detail?.shippingHeight
        ? newAdditionalInfo.push({
              title: product?.detail?.shippingHeight === 0 ? "" : "Package Height",
              value: product?.detail?.shippingHeight === 0 ? "" : product?.detail?.shippingHeight,
          })
        : "";

    product?.detail?.shippingWeight
        ? newAdditionalInfo.push({
              title: product?.detail?.shippingWeight === 0 ? "" : "Package Weight",
              value: product?.detail?.shippingWeight === 0 ? "" : product?.detail?.shippingWeight,
          })
        : "";

    const additionalInfoList: AdditionalInfoList = {
        additionalInfo: newAdditionalInfo,
    };

    const specificationsList = (product.attributeTypes ?? []).filter(attr => {
        return attr;
    });
    const newFeatureList = (product.specifications ?? []).filter(spec => {
        return spec.name === "Item Features";
    });
    // const featuresList = product?.specifications?.map(specification => {
    //     if (specification.name.toLowerCase() === "item features") {
    //         const value = specification.htmlContent.replace(/<[^>]*>?/gm, "").trim();
    //         const featuresList = value.split("\n");
    //         return featuresList;
    //     }
    // });

    function scrollTo(hash: string) {
        let scrollTop = 0;
        const navHeight = 90;
        const elem: HTMLElement | null = document.querySelector(`#${hash}`);
        scrollTop = elem ? elem.offsetTop - navHeight : 0;
        window.scrollTo({
            top: scrollTop,
            left: 0,
            behavior: "smooth",
        });
    }

    const productDescription = () => {
        if (product && product.content) {
            return (
                <StyledWrapper>
                    <Typography {...styles.titleCss}>Description</Typography>
                    <StyledWrapper {...styles.descriptionSection}>
                        {parse(product.content.htmlContent, parserOptions)}
                    </StyledWrapper>
                </StyledWrapper>
            );
        }
    };

    const specifications = (
        <StyledWrapper>
            <Typography {...styles.titleCss}>Specifications</Typography>
            {specificationsList?.map(attr => (
                <StyledWrapper {...styles.attributeTypeWrapper} key={attr.id.toString()}>
                    <StyledWrapper
                        css={css`
                            flex: 0 0 30%;
                        `}
                    >
                        <Typography {...styles.attributeTypeLabelText}>{attr.label || attr.name}</Typography>
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            flex: 0 0 30%;
                        `}
                    >
                        <Typography {...styles.attributeTypeValuesText}>
                            {attr.attributeValues!.map(attributeValue => attributeValue.valueDisplay).join(", ")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );

    const features = (
        <StyledWrapper>
            <Typography {...styles.titleCss}>Features</Typography>
            {/* <StyledUl>
                {featuresList &&
                    featuresList[0]?.map(feature => (
                        <StyledLi {...styles.featuresWrapper}>
                            <Icon src={CheckCircle} size={20} />
                            {feature.trim()}
                        </StyledLi>
                    ))}
            </StyledUl> */}
            {newFeatureList?.map(feature => (
                // eslint-disable-next-line react/jsx-key
                <StyledWrapper {...styles.featureLi}>
                    <>{parse(feature.htmlContent, parserOptions)}</>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );

    const additionalInfo = (
        <StyledWrapper>
            <Typography {...styles.titleCss}>Additional Info</Typography>
            {additionalInfoList?.additionalInfo?.map(attr => (
                <StyledWrapper {...styles.attributeTypeWrapper} key={attr.title.toString()}>
                    <StyledWrapper
                        css={css`
                            flex: 0 0 30%;
                        `}
                    >
                        <Typography {...styles.attributeTypeLabelText}>{attr?.title}</Typography>
                    </StyledWrapper>
                    <StyledWrapper
                        css={css`
                            flex: 0 0 30%;
                        `}
                    >
                        <Typography {...styles.attributeTypeValuesText}>{attr?.value}</Typography>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );

    const getYoutubeVideoId = (url: string) => {
        const youtubeRegex =
            /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?=.*v=\w+)(?:\S+)?|embed\/\w+|\S+\/\w+)|youtu\.be\/(\w+))(?:\S+)?$/;
        const match = url.match(youtubeRegex);
        return match ? match[1] : null;
    };

    const isYoutubeUrl = (url: string) => {
        const youtubeRegex =
            /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?=.*v=\w+)(?:\S+)?|embed\/\w+|\S+\/\w+)|youtu\.be\/(\w+))(?:\S+)?$/;
        return youtubeRegex.test(url);
    };

    const documents = (
        <StyledWrapper>
            <Typography {...styles.titleCss}>Documents</Typography>
            <StyledWrapper {...styles.wrapper}>
                <GridContainer {...styles.container}>
                    {product?.documents?.map(document => (
                        <GridItem key={document.id.toString()} {...styles.documentGridItem}>
                            {document.documentType === "URL" && isYoutubeUrl(document.filePath) ? (
                                <iframe
                                    width="100%"
                                    height="315"
                                    src={`https://www.youtube.com/embed/${getYoutubeVideoId(document.filePath)}`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                                ></iframe>
                            ) : (
                                <Link
                                    href={document.filePath}
                                    target="_new"
                                    {...styles.documentLink}
                                    data-test-selector={`productDetails_${document.id}`}
                                >
                                    <IconMemo {...styles.documentIcon} />
                                    <Typography {...styles.documentNameText}>{document.name}</Typography>
                                </Link>
                            )}
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );

    const navigationList: NavigationList = {
        navigation: [
            {
                title: product && product.content && product.content.htmlContent ? "Description" : "",
                link: pdpNavigationIds.description,
            },
            {
                title: specificationsList.length > 0 ? "Specifications" : "",
                link: pdpNavigationIds.specifications,
            },
            {
                title: newFeatureList.length > 0 ? "Features" : "",
                link: pdpNavigationIds.features,
            },
            {
                title: product && product?.documents && product?.documents.length > 0 ? "Documents" : "",
                link: pdpNavigationIds.documents,
            },
            {
                title: additionalInfoList.additionalInfo.length > 0 ? "Additional Info" : "",
                link: pdpNavigationIds.additionalInfo,
            },
        ],
    };

    return (
        <StyledWrapper {...styles.parentWrapper}>
            <StyledWrapper {...styles.navWrapper}>
                {navigationList.navigation.map(
                    (nav: Navigation, index: number) =>
                        (nav.link === pdpNavigationIds.specifications &&
                            product.attributeTypes &&
                            product.attributeTypes?.length <= 0) ||
                        (nav.title && (
                            <Button
                                onClick={() => scrollTo(nav.link)}
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                {...styles.navigationButton}
                            >
                                <Typography>{translate(nav.title)}</Typography>
                            </Button>
                        )),
                )}
            </StyledWrapper>
            {product && product.content && product.content.htmlContent && (
                <StyledWrapper {...styles.sectionWrapper} id="description">
                    {productDescription()}
                </StyledWrapper>
            )}
            {specificationsList.length > 0 && (
                <StyledWrapper {...styles.sectionWrapper} id="specifications">
                    {specifications}
                </StyledWrapper>
            )}
            {newFeatureList.length > 0 && (
                <StyledWrapper {...styles.sectionWrapper} id="features">
                    {features}
                </StyledWrapper>
            )}
            {product && product?.documents && product?.documents.length > 0 && (
                <StyledWrapper {...styles.sectionWrapper} id="documents">
                    {documents}
                </StyledWrapper>
            )}
            {additionalInfoList.additionalInfo.length > 0 && (
                <StyledWrapper {...styles.sectionWrapper} id="additionalInfo">
                    {additionalInfo}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailPageNavigation),
    definition: {
        displayName: "Product Page Navigation Custom",
        group: "Product Details",
        allowedContexts: [ProductDetailsPageContext],
    },
};

export default widgetModule;
